import Phaser from "phaser";
import { COLLISION_CELLS_PER_TILE, GROUND_ROW, defaultPlayersAbove, layerDepth, sortFraction, standDepthAt, standingDepth, type StandLookup, type WorldManifest } from "daedalus-core";
import { ChunkTexturePool, TileAnimator, createDomSurface, scenePageAtlas, type CoveringPiece, type EffectsDetail, type PageImage, type PieceAlpha, type PropView } from "daedalus-renderer-phaser";
import { Away, effectNumber, roofedAt, BODY_HEIGHT, BODY_WIDTH, Direction, SIGHT_CONE, SILHOUETTE, SIXTEENTHS, TICK_MS, WALK_SPEED, DEFAULT_WEATHER, placePuddles, regionOf, weatherRegionOf, regionWord, worldWeatherSeed, type CollisionGrid, type WetState, type NpcLook, type WireLook } from "odyssey-shared";
import type { LoadedWorld } from "../editor/world-loader.ts";
import type { CardFacts } from "./chat/hover-card.ts";
import type { GameInput, RoutedKey } from "../ui/input/router.ts";
import { ownOrPlain, type OwnLook } from "./character-layers.ts";
import { CharacterSprite, type CharacterFigure, type CharacterManifest } from "./character.ts";
import { CHUNK_LOADING } from "./chunk-loading.ts";
import { ChunkPainter } from "./chunk-painter.ts";
import type { NeighbourGround } from "./neighbours/ground.ts";
import { mountBeside } from "./neighbours/mount.ts";
import { CrossingStage } from "./neighbours/crossing-stage.ts";
import type { CharacterVisitor } from "./world-ui/characters.ts";
import { HarvestControl } from "./harvest/control.ts";
import { readHarvestMarks } from "./harvest/marks.ts";
import { PAGE_MAP_LIGHTS, applyKnownSources, readMapLights } from "./world-ui/lighting/map-lights.ts";
import { HARVEST_SHAKE_MS, HARVEST_SHAKE_PIXELS } from "./harvest/effects.ts";
import { HarvestLeaves } from "./harvest/leaves.ts";
import { bindHarvestStage, type HarvestStage } from "./harvest/stage-fx.ts";
import type { HarvestStore } from "./harvest/store.ts";
import { ShrineArt, type LandmarkTally } from "./map-picture/landmarks.ts";
import { GameMapPictures } from "./map-picture/pictures.ts";
import { CreatureHeads } from "./creature-heads.ts";
import { npcFigure, npcHead, npcName, npcPortrait } from "./npc-figures.ts";
import { CompanionFeed } from "./companion/feed.ts";
import { CompanionFollowers, type CompanionLabel } from "./companion/followers.ts";
import { CollisionDebug } from "./collision-debug.ts";
import type { SignInEnd } from "./disconnect.ts";
import { GROUND_ITEM_ROW, GroundView } from "./ground/view.ts";
import { PAGE_ICONS } from "./icons/page.ts";
import { InteractControl } from "./interact.ts";
import type { LookFigures } from "./looks/figures.ts";
import type { DialoguePortrait } from "../ui/kit/DialoguePanel.tsx";
import { createTargetLookup, watchNpcHeads, type TargetLookup } from "./interact-target.ts";
import type { ToolBelt } from "./belt/store.ts";
import { ABILITY_ACTIONS, TOOL_ACTIONS, abilityAction, type ActionId, type ToolActionId } from "./controls/actions.ts";
import { HeldKeys } from "./controls/held.ts";
import { keyedActions, type ActionHandler } from "./controls/keyed-actions.ts";
import { actionPress, heldOwned } from "./controls/press.ts";
import { pageKeys } from "./controls/page.ts";
import { stanceKeyAction } from "./fight/stance.ts";
import { bindGameKeys, movementKeys } from "./keyboard.ts";
import { jumpKeys } from "./jump/keys.ts";
import { stickOr } from "./touch/stick-or.ts";
import { isTouchPage } from "./touch/page.ts";
import type { StickSource } from "./touch/stick-mount.ts";
import type { TouchHandlers, TouchView } from "./touch/actions.ts";
import { TouchViewReader } from "./touch/touch-view.ts";
import type { RunAsk } from "./run/page.ts";
import { worldLoadFailure } from "./older-build.ts";
import { MessageLine } from "./message-line.ts";
import { PAGE_MOTION } from "./page-motion.ts";
import { serverNow } from "./server-clock.ts";
import { forcedFor, weatherFeed, type ForcedByMap, type ForcedWeather, type ForcedWind } from "./weather/weather-feed.ts";
import { WEATHER_FADE_MS } from "./weather/weather-fade.ts";
import { WeatherHandover } from "./weather/weather-handover.ts";
import { clearWeather, publishWeather } from "./weather/weather-report.ts";
import { WeatherView, type WeatherStand } from "./weather/weather-view.ts";
import type { FeetMode, FigureVisit } from "./weather/puddle-feet.ts";
import { puddleBlockedAt, puddleGroundOf } from "./weather/puddle-world.ts";
import { dripLevelOf } from "./weather/drip-level.ts";
import { PresenceProps, presenceEntries } from "./presence.ts";
import { PropFade } from "./prop-fade.ts";
import { Silhouette } from "./silhouette.ts";
import { Concealment, concealReadings } from "./concealment.ts";
import type { SceneDirector } from "./bench/scene-director.ts";
import type { DrawnCharacter } from "./remote-characters.ts";
import { OthersFeed, minimapOthersReading } from "./minimap/others-feed.ts";
import { placeName } from "./minimap/place-line.ts";
import type { MinimapFeed } from "./minimap/store.ts";
import { createIdleDrawer, frameIdleScheduler } from "./world-map/idle.ts";
import { createWorldMapPicture, mapTitle } from "./world-map/dom.ts";
import type { WorldMapPicture } from "./world-map/picture.ts";
import type { WorldMapSession } from "./world-map/session.ts";
import type { WorldMapStore } from "./world-map/store.ts";
import { fadeScreen } from "./screen-fade.ts";
import { gameReport } from "./report.ts";
import { PAGE_RULES } from "./rules.ts";
import { ownNow } from "./own-clock.ts";
import { say } from "../words.ts";
import { ProjectileView } from "./world/projectile-view.ts";
import { chunksCovering, readyToReveal } from "./reveal.ts";
import type { GameConnection } from "./connection.ts";
import type { LeaveAnswer } from "./leave-desk.ts";
import { GameSession, type SessionOptions, type SessionWorld } from "./session.ts";
import { WorldBoot, type BootedWorld, type WorldBootOptions } from "./world-boot.ts";
import { WorldThings } from "./world-things.ts";
import { followWorldZoom, snapScrollToDevicePixels, type WorldZoomSource } from "./world-zoom.ts";
import { WORLD_UI_MODULES } from "./world-ui/modules.ts";
import { HEAD_ABOVE_FEET } from "./world-ui/characters.ts";
import type { ThingSource } from "./world-ui/things.ts";
import { NO_GROUND, type GroundSource } from "./world-ui/ground.ts";
import { LabelKey } from "./world-ui/ground-labels/visible.ts";
import { gameBanner } from "../console-banner.ts";
import { mountWorldUi, preloadWorldUi } from "./world-ui/mount.ts";
import type { UiScaleSource } from "./world-ui/ui-scale.ts";
import { indoorOfMap } from "./map-indoor.ts";

/**
 * The game scene (P0.4-T4, ADR-032): loads the published world's client chunks, assembles the collision
 * grid, connects to the gateway, predicts the local player, interpolates everyone else and draws all of them
 * as layered character sprites (ADR-036) over the chunk tiles and among the props, which sort with the
 * characters by row. The camera follows the page's world zoom, a whole number or 2.5, which `world-ui/sharp-zoom/`
 * draws at 3 and resamples, and keeps the world on the device pixel grid (`world-zoom.ts`, CLIENT.md rendering rules). Everything the scene adds lands in the world layer, and
 * the canvas surfaces in `world-ui/modules.ts` draw in the UI layer through a second camera at the UI scale
 * (`world-ui/mount.ts`, CLIENT.md section 7.2).
 *
 * The scene runs the Phaser lifecycle and the frame; its parts live beside it: the join's session and the frames
 * the server sends (`session.ts`), the local player (`local-player.ts`) and the other players
 * (`remote-characters.ts`), the chunk painting (`chunk-painter.ts`), the things (`world-things.ts`), the occluder
 * fade (`prop-fade.ts`), E and its replies (`interact.ts`), the message line (`message-line.ts`) and the F8
 * collision debug view (`collision-debug.ts`); the shots in flight and their bursts are `world/projectile-view.ts`.
 *
 * Joining is the loading shell's (P1.4-T5): the scene tells the shell each step of the world's load
 * (`world-boot.ts`), the connection and the welcome (`session.ts`), and reveals the world only on a drawn frame that
 * shows the local character over every chunk its camera saw, after the first snapshot (`reveal.ts`); `ready` in
 * the report is that reveal, not the welcome. A failure before the reveal is the shell's notice: a file that did
 * not arrive, or a join that failed (`session.ts`). Retry resumes from what failed: a failed load fetches only what
 * is missing, and a failed join connects again, presenting the page's newest token; a close whose notice reloads
 * the page (`disconnect.ts`) offers Reload instead.
 *
 * A close after the reveal takes the player out of the world (P1.9-T15): the session is forgotten and the loading
 * shell shows the disconnect notice, and the page hides the HUD, the chat and the menu behind it. Reconnect is
 * Retry after the reveal: the loaded world is reused and only the join is made again. Nothing joins again by
 * itself.
 */

const MAP_ID = "main";

/**
 * The longest the reveal waits, from the first frame it could otherwise show, for the own character's look to be
 * composed, in ms. Past it the world shows with the character in the fallback look.
 */
const OWN_LOOK_WAIT_MS = 2000;

/**
 * What the page hands the scene: what the session takes (`session.ts`), with where the world loads from, the
 * world zoom and UI scale it draws at, and the input router. `reading` is also asked each frame whether the thing
 * it shows still reaches the character. `running` names the world version the page's map server runs, which the
 * world boot loads in place of the pointer's (ADR-050's amendment of 2026-10-02). `director`, given only in the benchmark
 * mode (`?bench=1`, P2.0-T4), may centre the camera and walk the character in place of the player (`bench/scene-director.ts`).
 * `stick`, given on a touch page, is the virtual stick (`touch/`, P1.9-T51f): while it sends a direction it is the
 * movement intent, and otherwise the keys are.
 * While `moderation` says a GM froze the player, the scene reads no movement, since the gateway drops it
 * (ADR-056's amendment of 2026-10-04, item 2). `minimap`, when the page has one, is told the character's drawn position in
 * tiles each frame (P1.9-T52). `worldMap`, when the page has one, is given the picture of the booted map, the character's
 * position and heading each frame, and a frame to draw a chunk of its picture in while the window is open (P2.12-T6).
 */
export interface GameOptions extends SessionOptions {
  readonly harvest?: HarvestStore;
  readonly base: string;
  readonly worldId: string;
  readonly zoom: WorldZoomSource;
  readonly uiScale: UiScaleSource;
  readonly pointer: "current" | "published";
  readonly input: GameInput;
  readonly running?: WorldBootOptions["running"];
  readonly ownLook?: WorldBootOptions["ownLook"];
  readonly director?: SceneDirector;
  readonly stick?: StickSource;
  readonly weatherForce?: ForcedByMap<ForcedWeather> | null;
  readonly wetForce?: ForcedByMap<WetState> | null;
  readonly now?: () => number | null;
  readonly feetMode?: FeetMode;
  readonly drenchedForce?: number | null;
  readonly ownDrench?: (nowMs: number) => number;
  readonly windForce?: ForcedWind | null;
  readonly cloudsForce?: "fair" | "cloudy" | null;
  readonly effects?: () => EffectsDetail;
  readonly looks?: LookFigures;
  readonly ownAppearance?: WorldBootOptions["ownAppearance"];
  readonly minimap?: MinimapFeed;
  /**
   * Whether the page is on low detail (a reduced render resolution), which hides other players' companions first (ADR-142).
   */
  readonly lowDetail?: () => boolean;
  /**
   * Hears a context menu request on another player (a right click, or a long press on touch): the player's two names
   * and the point, in client CSS pixels, the menu opens at (P2.13-T5, ADR-101).
   */
  readonly playerMenu?: (who: { readonly firstName: string; readonly familyName: string }, x: number, y: number) => void;
  readonly worldMap?: WorldMapSession & Pick<WorldMapStore, "setWorld" | "clearWorld" | "setPosition" | "pinNow" | "frame" | "prefetch" | "onPlaces">;
}

/**
 * The loaded world as the scene draws it, made once when the world first boots.
 */
interface Stage extends SessionWorld {
  readonly world: LoadedWorld;
  readonly map: WorldManifest["maps"][number];
  readonly grid: CollisionGrid;
  readonly manifest: CharacterManifest;
  readonly ownLook: OwnLook | null;
  readonly animator: TileAnimator;
  readonly painter: ChunkPainter;
  readonly beside: NeighbourGround;
  readonly harvest: HarvestStage | null;
  readonly fade: PropFade;
  readonly concealment: Concealment;
  readonly silhouette: Silhouette;
  readonly projectiles: ProjectileView;
  readonly band: number;
  readonly stand: StandLookup | null;
  readonly weather: WeatherView;
}

/**
 * The scene.
 */
export class GameScene extends Phaser.Scene {
  private readonly worldBoot: WorldBoot;
  private readonly crossing: CrossingStage;
  private readonly session: GameSession;
  private readonly messageLine: MessageLine;
  private readonly interaction: InteractControl;
  private readonly debug = new CollisionDebug(this);
  private readonly minimapOthers = new OthersFeed();

  /**
   * An NPC's look in the loaded world's NPC look table, for the minimap's marks; made once so a frame allocates
   * nothing for it.
   *
   * @param appearance The NPC's appearance.
   * @returns The look, or null before the world loads or past the table's end.
   */
  private readonly npcLookOf = (appearance: number): NpcLook | null => this.stage?.npcLooks[appearance] ?? null;

  /**
   * Visits the other maps' figures for the minimap's marks (ADR-147); made once so a frame allocates nothing for it.
   *
   * @param visit The visitor.
   */
  private readonly besideOthers = (visit: CharacterVisitor): void => this.session.crossing.visit(visit);
  private stage: Stage | null = null;
  private readonly stageEnds: (() => void)[] = [];
  private silhouette: Silhouette | null = null;
  /**
   * Carries the weather view through a seam crossing's change of stage (ADR-147 note 15).
   */
  private readonly weatherHandover = new WeatherHandover<WeatherView>();
  private heads: CreatureHeads | null = null;
  private ownLookSince = -1;
  private companions: CompanionFollowers | null = null;
  private companionFeed: CompanionFeed | null = null;

  /**
   * Where the top of each thing's art is drawn, for the world UI, read from the world's things once it has loaded.
   */
  private readonly thingTops: ThingSource = {
    top: (handle, out) => this.stage?.things.top(handle, out) ?? false,
  };
  private readonly groundItems: GroundSource = {
    readings: () => this.stage?.ground.readings ?? NO_GROUND.readings(),
  };
  private intent: (() => Direction) | null = null;
  private run: RunAsk | null = null;

  /**
   * What each touch action button calls, set once the scene is created: the functions the keys call, so a key and a tap
   * cannot disagree (`touch/actions.ts`).
   */
  public touchHandlers: TouchHandlers | null = null;
  private readonly touchReader = new TouchViewReader({ context: () => this.interaction.context(), run: () => this.options.run, stance: () => this.session.stance, belt: () => this.options.belt });
  private readonly pressed = new Phaser.Math.Vector2();

  /**
   * Every prop view the world draws props in, by key: each prop layer's under its layer id and the things' under
   * `things:` and their layer's index. The chunk painter, the things and the occluder fade share it; browser suites
   * read it through `window.phaserGame` to see what each view shows.
   */
  public readonly props = new Map<string, PropView>();

  /**
   * The things drawn, which browser suites read through `window.phaserGame`.
   *
   * @returns The things, or null before the world has loaded.
   */
  public get things(): WorldThings | null {
    return this.stage?.things ?? null;
  }

  /**
   * The presence props, which browser suites read through `window.phaserGame`.
   *
   * @returns The presence props, or null before the world has loaded.
   */
  public get presence(): PresenceProps | null {
    return this.stage?.presence ?? null;
  }

  /**
   * The local player's handle, which browser suites read through `window.phaserGame`.
   *
   * @returns The handle, or -1 outside a session.
   */
  public get handle(): number {
    return this.session.handle;
  }

  /**
   * The current connection, which the performance panel reads (ADR-087).
   *
   * @returns The connection, or null between connections.
   */
  public get connection(): GameConnection | null {
    return this.session.connection;
  }

  /**
   * The local character's sprite, which browser suites read through `window.phaserGame`.
   *
   * @returns The sprite, or null outside a session.
   */
  public get local(): CharacterFigure | null {
    return this.session.local.sprite;
  }

  /**
   * The look the page's own character holds, as it last arrived (ADR-094 decision 6), which the temporary Change look
   * screen opens on.
   *
   * @returns The look, or undefined outside a session and before the own look has arrived.
   */
  public ownLook(): Readonly<WireLook> | undefined {
    return this.session.ownLook();
  }

  /**
   * The composed looks, which browser suites and rigs read through `window.phaserGame`.
   *
   * @returns What draws composed characters, or null on a page that was handed none.
   */
  public get looks(): LookFigures | null {
    return this.options.looks ?? null;
  }

  /**
   * The staff badge the view holds for an entity, which a Local line or an action in the chat log carries (ADR-112
   * decision 5.4).
   *
   * @param handle The entity's handle.
   * @returns The badge, 0 to 15; 0 for none and for a handle the view no longer holds.
   */
  public badgeOf(handle: number): number {
    return this.session.connection?.view.entities.get(handle)?.badge ?? 0;
  }

  /**
   * What the chat's hover card lists for a character in the view (ADR-068's amendment of 2026-10-27): its level and its
   * away state.
   *
   * @param handle The entity's handle.
   * @returns The facts, or null for a handle the view no longer holds.
   */
  public cardFactsOf(handle: number): CardFacts | null {
    const state = this.session.connection?.view.entities.get(handle);
    return state === undefined ? null : { level: state.level, away: state.away ?? 0 };
  }

  /** The chunk textures a stage's painters hand on to the next stage's at a seam crossing (`CHUNK_LOADING.handover`). */
  private readonly chunkTextures = new ChunkTexturePool(CHUNK_LOADING.handover, CHUNK_LOADING.handoverMs, () => performance.now());

  private figureVisit: FigureVisit | null = null;
  private readonly visitRemote = (drawn: DrawnCharacter, handle: number): void => {
    this.figureVisit?.(handle, drawn.sprite);
  };

  /** The Drenched effect's number in the page's rules. */
  private readonly drenchedNumber = effectNumber(PAGE_RULES, "drenched");

  /**
   * How wet a drawn figure is for its drip (`dripLevelOf`): the player's own from `ownDrench` or the development force.
   *
   * @param id The figure's id, 0 for the player's own, else its handle.
   * @returns 0 dry to 1 drenched.
   */
  private readonly drenchedLevel = (id: number): number => dripLevelOf(id, this.drenchedNumber, () => this.options.drenchedForce ?? this.options.ownDrench?.(ownNow()) ?? 0, (handle) => this.session.connection?.view.entities.get(handle)?.buffs);

  /**
   * Hands every character drawn to a visitor: the page's own, as handle 0, then every other by its handle. For the effects at
   * the feet of what stands in a puddle and the drip under a Drenched character.
   *
   * @param visit The visitor.
   */
  private readonly eachFigure = (visit: FigureVisit): void => {
    const own = this.session.local.sprite;
    if (own !== null) visit(0, own);
    this.figureVisit = visit;
    this.session.remotes.drawn.forEach(this.visitRemote);
  };

  /**
   * The other players' characters drawn, by handle, which browser suites read through `window.phaserGame`.
   *
   * @returns The characters.
   */
  public get characters(): ReadonlyMap<number, DrawnCharacter> {
    return this.session.remotes.drawn;
  }

  /**
   * The companions drawn, which browser suites read through `window.phaserGame`.
   *
   * @returns The companions, null before a map's first frame.
   */
  public get companionsDrawn(): CompanionFollowers | null {
    return this.companions;
  }

  /**
   * @param options Connection details, the loading shell's progress and what a refused token calls.
   */
  public constructor(private readonly options: GameOptions) {
    super("game");
    this.worldBoot = new WorldBoot(this, { base: options.base, worldId: options.worldId, pointer: options.pointer, mapId: MAP_ID, progress: options.progress, ...(options.running === undefined ? {} : { running: options.running }), ...(options.ownLook === undefined ? {} : { ownLook: options.ownLook }), ...(options.looks === undefined ? {} : { looks: options.looks }), ...(options.ownAppearance === undefined ? {} : { ownAppearance: options.ownAppearance }) });
    this.crossing = new CrossingStage(this.worldBoot, {
      stage: () => this.stage,
      restage: (booted) => {
        const started = performance.now();
        this.weatherHandover.run(
          this.stage?.weather ?? null,
          () => {
            this.endStage(true);
            this.prepare(booted);
          },
          () => clearWeather(gameReport.weather, gameReport.lighting),
        );
        gameReport.restageMs = performance.now() - started;
      },
    });
    this.messageLine = new MessageLine(this, options.hud);
    this.session = new GameSession(options, {
      world: () => this.stage,
      character: () => this.character(),
      ownCharacter: () => this.ownCharacter(),
      ownHead: () => this.stage?.ownLook?.head ?? this.playerHead(),
      playerHead: () => this.playerHead(),
      wear: (figure, look) => this.options.looks?.wear(figure, look),
      creature: (appearance, handle) => this.creature(appearance, handle),
      creatureHead: (appearance) => this.creatureHead(appearance),
      npc: (appearance) => this.npc(appearance),
      npcHead: (appearance) => this.npcHead(appearance),
      now: () => this.game.loop.time,
      interaction: (interaction) => this.interaction.receive(interaction),
      messageLine: this.messageLine,
      intent: () => this.intent?.() ?? Direction.None,
      maps: {
        current: () => this.stage?.map.id ?? null,
        enter: (mapId) => this.enterMap(mapId),
        fade: (direction, ms) => this.fadePage(direction, ms),
        holds: (mapId) => this.stage?.beside.holds(mapId) === true,
        boot: (mapId) => this.crossing.bootFor(mapId),
        ready: (mapId) => this.crossing.ready(mapId),
        cross: (mapId) => this.crossing.cross(mapId),
        drop: () => this.crossing.drop(),
        showNeighbour: (mapId, shown) => this.stage?.beside.show(mapId, shown),
      },
    });
    this.interaction = new InteractControl(
      {
        lookup: (): TargetLookup | null => this.stage?.lookup ?? null,
        connection: () => this.session.connection,
        predicted: () => this.session.local.prediction?.predicted,
        limits: () => this.session.local.effectLimits,
        talking: () => this.options.dialogue?.open ?? false,
      },
      options.input,
      options.reading,
      this.messageLine,
      options.chest,
      this.session.gate,
      this.session.ground.pickUp,
      options.harvest === undefined ? undefined : new HarvestControl(options.harvest, { rules: PAGE_RULES, selectedTool: () => options.belt?.selectedItem() ?? null, bag: () => options.bag?.getSnapshot().slots ?? [], chunkSize: () => this.stage?.world.manifest.chunkSize ?? 16 }),
    );
    const session = this.session;
    this.interaction.useDoor({ get shown() { return session.door.shown; }, get selected() { return session.targets.handle; } });
  }

  /**
   * Hides a prop on the canvas, or draws it again, for the page's harvesting (ADR-142 decision 3.5). Nothing happens
   * before the world is drawn.
   *
   * @param id The prop's authored id.
   * @param withheld True to hide it.
   * @param fadeMs How long the change eases, in milliseconds.
   */
  public withholdProp(id: string, withheld: boolean, fadeMs: number): void {
    this.stage?.painter.withhold(id, withheld, fadeMs);
  }

  /**
   * Queues what the canvas surfaces load.
   */
  public preload(): void {
    preloadWorldUi(this.load, WORLD_UI_MODULES);
  }

  /**
   * Mounts the world and UI layers with their cameras, hands the keyboard to the input router, which lets
   * the scene's keys through only while the game owns it (`ui/input/router.ts`), watches each drawn frame
   * for the reveal, then loads the world and connects; a left click on the world selects what stands under it and,
   * while the weapon is drawn, swings toward the pointer (P2.1-T4, P2.2-T3, P2.2-T13), and Escape with no layer open
   * clears a target before it asks for the game menu. Each of the scene's actions answers the key the page's key
   * table binds it to (P1.9-T50, `controls/`), from the next press after the table changes; by default F8 toggles the
   * collision debug view, E interacts, R sheathes or draws the weapon (P2.2-T13), Tab selects the nearest the player
   * may attack and then the next (Shift with it the other way), and keys 1 to 9 and 0 use the ability bar's slots
   * toward the pointer (P2.2-T4); Z held shows every ground item's name (P2.8-T76), all of them rebindable.
   */
  public create(): void {
    mountWorldUi(this, this.options.uiScale, WORLD_UI_MODULES, this.session.characters, this.interaction.target, this.thingTops, this.groundItems);
    const pressed = new HeldKeys();
    const keyboard = movementKeys(this, this.options.input, pageKeys, pressed);
    const stick = this.options.stick;
    const keys = stick === undefined ? keyboard : () => stickOr(stick, keyboard);
    const director = this.options.director;
    const held = director === undefined ? keys : () => director.intent() ?? keys();
    const moderation = this.options.moderation;
    this.intent = moderation === undefined ? held : () => (moderation.frozen() ? Direction.None : held());
    this.bindRun(pressed);
    this.bindLabelKey();
    const handlers: Partial<Record<ActionId, ActionHandler>> = {
      collision: () => this.debug.toggle(),
      interact: (event) => {
        if (event.defaultPrevented) {
          return false;
        }
        this.interactNow();
        return true;
      },
      target: (event) => this.cycleTarget(event),
      stance: (event) => this.toggleStance(event),
    };
    for (let slot = 0; slot < ABILITY_ACTIONS; slot += 1) {
      handlers[abilityAction(slot)] = (event) => this.useSlotKey(slot, event);
    }
    handlers.toolCycle = (event) => this.toolKey(event, (belt) => belt.cycle());
    this.touchHandlers = { attack: () => this.attackNow(), interact: () => this.interactNow(), jump: () => this.jumpNow(), sprint: () => void this.options.run?.touchToggle(), stance: () => void this.stanceNow(), tool: () => void this.toolNow((belt) => { belt.toggleFan(); return true; }) };
    for (let place = 1; place <= TOOL_ACTIONS; place += 1) {
      handlers[`tool${place}` as ToolActionId] = (event) => this.toolKey(event, (belt) => belt.press(place));
    }
    handlers.companion = (event) => this.companionKey(event);
    bindGameKeys(this, this.options.input, keyedActions(pageKeys, handlers), pressed);
    this.bindJump();
    const stopEscape = this.options.input.onEscape(() => this.session.targets.clear(this.game.loop.time));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, stopEscape);
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPress);
    this.game.canvas.addEventListener("contextmenu", this.onContextMenu);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.game.canvas.removeEventListener("contextmenu", this.onContextMenu));
    this.game.events.on(Phaser.Core.Events.POST_RENDER, this.afterRender);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.game.events.off(Phaser.Core.Events.POST_RENDER, this.afterRender));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.options.looks?.destroy());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.endStage(false));
    void this.start();
  }

  /**
   * Gives Space its hop (ADR-116 decisions 17 to 19, `jump/keys.ts`): the own character plays `jump` on the press and
   * the next input carries the bit, at most once every `jump_ticks`, never while dead, held by a freeze or in its grace.
   */
  private bindJump(): void {
    const stop = this.options.input.onKey(
      jumpKeys({
        hop: () => this.session.local.hop(this.game.loop.time, PAGE_RULES.constants.jump_ticks * TICK_MS),
        dead: () => this.session.local.dead,
        held: () => this.jumpHeld(),
      }),
    );
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, stop);
  }

  /**
   * Whether a Game Master's freeze holds the character, its grace included, in which it may not hop.
   *
   * @returns True while held.
   */
  private jumpHeld(): boolean {
    return this.options.moderation?.frozen() === true || this.session.connection?.view.entities.get(this.session.handle)?.away === Away.Held;
  }

  /**
   * Jump as the touch button asks it, what Space does: the hop, never while dead or held.
   */
  private jumpNow(): void {
    if (!this.session.local.dead && !this.jumpHeld()) {
      this.session.local.hop(this.game.loop.time, PAGE_RULES.constants.jump_ticks * TICK_MS);
    }
  }

  /**
   * A press on the door's prompt in the HUD: what E does.
   */
  public interactFromUi(): void {
    this.interactNow();
  }

  /**
   * E as the key and the touch Interact button ask it: acts on what `InteractControl.context` names, never while dead.
   */
  private interactNow(): void {
    if (!this.session.local.dead) {
      this.interaction.interact();
    }
  }

  /**
   * Gives the page's run its keys (ADR-116 decisions 8 and 11; ADR-061's amendment for P1.9-T174, point 3): Sprint's
   * key held is read from the physical keys held beside Move's, so it is let go wherever they are; the toggle's switch
   * watches every key the router hears go down or up, and is disarmed when the window loses focus. The run is then asked
   * once for each input sent. A page given no run never asks.
   *
   * @param held The physical keys held, which Move reads too.
   */
  private bindRun(held: HeldKeys): void {
    const run = this.options.run;
    if (run === undefined) {
      return;
    }
    this.run = { asks: () => run.asks(held.heldAs(pageKeys, "run")), allowed: (limits, dead) => run.allowed(limits, dead) };
    const unwatch = this.options.input.watchKeys((event, down, owned) => run.key(pageKeys.actionOf(event) === "run", down, down ? heldOwned(event, owned) : owned, event.repeat));
    const lost = (): void => run.lost();
    const pressed = (): void => run.pressed();
    this.game.events.on(Phaser.Core.Events.BLUR, lost);
    window.addEventListener("pointerdown", pressed, true);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      unwatch();
      window.removeEventListener("pointerdown", pressed, true);
      this.game.events.off(Phaser.Core.Events.BLUR, lost);
      this.run = null;
    });
  }

  /**
   * Gives the ground item labels their key (P2.8-T76, `world-ui/ground-labels/visible.ts`): Show item names' key (Z
   * unless rebound in Controls) held while the game owns the keyboard shows every ground item's name on screen, written to the labels' report, which they read each frame;
   * the hold ends when the key goes up or the window loses focus.
   */
  private bindLabelKey(): void {
    const key = new LabelKey(gameReport.groundLabels, pageKeys);
    const unwatch = this.options.input.watchKeys((event, down, owned) => key.key(event.key, event.code, down, owned, event.target));
    const lost = (): void => key.lost();
    this.game.events.on(Phaser.Core.Events.BLUR, lost);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      unwatch();
      this.game.events.off(Phaser.Core.Events.BLUR, lost);
      key.lost();
    });
  }

  /**
   * Target nearest (Tab by default): selects the nearest the player may attack, and the next on each press, Shift with
   * the key the other way, while the world is shown and the character lives, whatever surface that is not modal is
   * open (ADR-061's amendment of 2026-10-03); otherwise the key is left alone. Whichever action holds Tab acts only
   * while the game has focus (`controls/press.ts`), so a keyboard player still moves through the HUD's buttons, a
   * panel's controls and the death screen's button.
   *
   * @param event The press.
   * @returns False when the key was left alone.
   */
  private cycleTarget(event: RoutedKey): boolean {
    if (!this.session.revealed || this.session.local.dead) {
      return false;
    }
    this.session.targets.cycle(this.game.loop.time, event.shiftKey === true ? -1 : 1);
    return true;
  }

  /**
   * The stance's key (R by default; P2.2-T13, `fight/stance.ts`): sheathes or draws the weapon while the world is shown, whatever surface that is not
   * modal is open (ADR-061's amendment of 2026-10-03); an auto-repeat is taken and does nothing, and a press with Ctrl,
   * Alt or Meta is left to the browser, so Ctrl+R still reloads on the default key. Otherwise the key is left alone.
   *
   * @param event The press.
   * @returns False when the key was left alone.
   */
  private toggleStance(event: RoutedKey): boolean {
    const action = stanceKeyAction(event, (press) => actionPress(pageKeys, "stance", press));
    if (action === "leave" || !this.session.revealed) {
      return false;
    }
    if (action === "toggle") {
      this.stanceNow();
    }
    return true;
  }

  /**
   * Sheathes or draws the weapon: what R does and what the touch Stance button does, while the world is shown.
   *
   * @returns False while the world is not shown.
   */
  private stanceNow(): boolean {
    if (!this.session.revealed) {
      return false;
    }
    this.session.toggleStance();
    return true;
  }

  /**
   * The companion key (ADR-142 decision 6.2, unbound until a player chooses one): sends him out or home, while the world
   * is shown; an auto-repeat, and any press while the character is dead, is taken and does nothing.
   *
   * @param event The press.
   * @returns False when the key was left alone.
   */
  private companionKey(event: RoutedKey): boolean {
    const companion = this.options.companion;
    if (companion === undefined || !this.session.revealed) {
      return false;
    }
    if (!event.repeat && !this.session.local.dead) {
      companion.toggle();
    }
    return true;
  }

  /**
   * A tool belt key (ADR-142 decision 3b: Q cycles the filled slots, then none; Tool 1 to Tool 4 take a slot's tool in hand or
   * put it away), while the world is shown, whatever surface that is not modal is open; an auto-repeat, and any press
   * while the character is dead, is taken and does nothing, so a held key never presses again. Otherwise the key is left
   * alone.
   *
   * @param event The press.
   * @param act What the key does to the belt.
   * @returns False when the key was left alone.
   */
  private toolKey(event: RoutedKey, act: (belt: ToolBelt) => boolean): boolean {
    return event.repeat ? this.options.belt !== undefined && this.session.revealed : this.toolNow(act);
  }

  /**
   * What a tool key does and what the touch Tool button does: acts on the belt while the world is shown and the character
   * lives.
   *
   * @param act What to do to the belt.
   * @returns False when there is no belt or the world is not shown.
   */
  private toolNow(act: (belt: ToolBelt) => boolean): boolean {
    const belt = this.options.belt;
    if (belt === undefined || !this.session.revealed) {
      return false;
    }
    if (!this.session.local.dead) {
      act(belt);
    }
    return true;
  }

  /**
   * The touch Attack button, which has no key: the basic blow at the target or along the facing (`Fight.attack`), never
   * while dead.
   */
  private attackNow(): void {
    if (!this.session.local.dead) {
      this.session.attack();
    }
  }

  /**
   * A slot's key (P2.2-T4, question 4): uses the slot toward the world point under the pointer, as a left click uses
   * slot 1, or, for a displacement, the way the movement keys hold or else along the facing (`fight/fight.ts`), while
   * the world is shown, whatever surface that is not modal is open (ADR-061's amendment of 2026-10-03: only a focused
   * text field or a modal's hold keeps a key from the scene); an auto-repeat, or any press while the character is
   * dead (the death screen holds the world's input), is taken and does nothing, so a held key never presses again.
   * Otherwise the key is left alone.
   *
   * @param slot The slot's index, from 0.
   * @param event The press.
   * @returns False when the key was left alone.
   */
  private useSlotKey(slot: number, event: RoutedKey): boolean {
    if (!this.session.revealed) {
      return false;
    }
    if (!event.repeat && !this.session.local.dead) {
      gameReport.slotKey = slot;
      const pointer = this.input.activePointer;
      const at = this.cameras.main.getWorldPoint(pointer.x, pointer.y, this.pressed);
      this.session.useSlot(slot, at.x, at.y);
    }
    return true;
  }

  /**
   * A press on the canvas: a left press on a ground item's drawn square picks that item up and does nothing else, once
   * the world is shown (P2.8-T65); a left press on a shrine or a waypoint in reach asks for travel as E does (ADR-137
   * decision 6); any other left press selects what stands there and, while the weapon is drawn, swings toward the
   * world point under the pointer (P2.2-T13), read through the world camera, which keeps the world zoom for pointer
   * input at every zoom (CLIENT.md 7.6). Presses on the HUD's menu
   * button, the one part of the overlay that takes the pointer, never reach the canvas, and neither do presses on the
   * death screen, which covers the world while the character is dead; a press while dead does nothing.
   *
   * @param pointer The pointer.
   */
  private readonly onPress = (pointer: Phaser.Input.Pointer): void => {
    if (pointer.button === 0) {
      this.pressAt(pointer.x, pointer.y, !(pointer.wasTouch && isTouchPage()));
    }
  };

  /**
   * Whether the touch controls may show: the world is revealed and the character is in no transfer (a door or map handoff
   * or a held reconnect), the in-world signal the HUD's own reveal follows.
   *
   * @returns True while the controls may be drawn.
   */
  public touchLive(): boolean {
    return this.session.revealed && !this.session.inTransfer;
  }

  /**
   * What the touch buttons draw now (`touch/touch-view.ts`).
   */
  public readonly touchView = (): TouchView => this.touchReader.read();

  /**
   * A tap the stick's zone hands to the world (P1.9-T51f): the press `onPress` takes, at the place of the finger. The
   * zone lies over the canvas, so the finger's own touch events never reach Phaser.
   *
   * @param clientX The tap's x in CSS pixels of the page.
   * @param clientY The tap's y in CSS pixels of the page.
   */
  public tapWorld(clientX: number, clientY: number): void {
    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      this.pressAt(((clientX - rect.left) * canvas.width) / rect.width, ((clientY - rect.top) * canvas.height) / rect.height, false);
    }
  }

  /**
   * The left press itself, at a point of the canvas in game pixels: a press on the world prompt does what E does
   * (ADR-061's amendment of 2026-10-31, decision 12), on every device; any other press picks up, travels or selects, and
   * swings only when `swings`, which a finger on a touch page does not (decision 8: Attack is the button).
   *
   * @param x The point's x.
   * @param y The point's y.
   * @param swings Whether the press also swings the weapon drawn.
   */
  private pressAt(x: number, y: number, swings: boolean): void {
    if (this.session.local.dead) {
      return;
    }
    if (this.session.revealed && gameReport.prompt.hit(x, y)) {
      this.interactNow();
      return;
    }
    const at = this.cameras.main.getWorldPoint(x, y, this.pressed);
    const tileSize = this.stage?.tileSize ?? 0;
    if (tileSize > 0 && this.session.revealed && this.session.ground.pickUp.at((at.x * SIXTEENTHS) / tileSize, (at.y * SIXTEENTHS) / tileSize)) {
      return;
    }
    if (this.session.revealed && this.interaction.clickTravel(at.x, at.y)) {
      return;
    }
    if (swings) {
      this.session.swing(at.x, at.y);
    } else {
      this.session.select(at.x, at.y);
    }
  }

  /**
   * A context menu request on the canvas, a right click or a long press on touch: opens the player menu on the other
   * player whose nameplate or body holds the pointer, once the world is shown and while the character is alive. The browser's own
   * menu is already kept off the page.
   *
   * @param event The request.
   */
  private readonly onContextMenu = (event: MouseEvent): void => {
    const open = this.options.playerMenu;
    if (open === undefined || this.session.local.dead || !this.session.revealed) {
      return;
    }
    const pointer = this.input.activePointer;
    const at = this.cameras.main.getWorldPoint(pointer.x, pointer.y, this.pressed);
    const who = this.session.targets.playerOf(gameReport.nameplates.plateAt(pointer.x, pointer.y)) ?? this.session.targets.playerAt(at.x, at.y);
    if (who !== null) {
      open(who, event.clientX, event.clientY);
    }
  };

  /**
   * Loads what the world still lacks, prepares the scene for it once, and connects. A load that fails is
   * the world's failure on the loading shell, its message on the detail line and in the report's `error`; a
   * world written for a newer build than this page's reloads the page instead, once per world version, and says
   * the game has been updated when that reload was held (`older-build.ts`).
   */
  private async start(): Promise<void> {
    let booted: BootedWorld;
    try {
      booted = await this.worldBoot.runMap(this.stage?.map.id ?? MAP_ID);
      this.prepare(booted);
    } catch (error) {
      const failure = worldLoadFailure(error, this.worldBoot.loadedVersion());
      if (failure !== null) {
        this.session.fail(failure);
      }
      return;
    }
    this.session.connect();
  }

  /**
   * Retries what failed before the reveal, from the loading shell's Retry: a load resumes where it stopped
   * and a join connects again. After the page left a world it showed, this is Reconnect (P1.9-T15): the
   * world's load is in hand, so only the join is made again. A failure that only a reload mends (a world
   * version the server had moved past, a stale page) offers Reload instead and never comes here. Once the
   * sign-in has ended nothing joins.
   */
  public retry(): void {
    if (!this.session.retryable) {
      return;
    }
    gameReport.error = null;
    gameReport.closed = null;
    void this.start();
  }

  /**
   * Departs the world for character select (P1.9-T62): the page leaves the world with no notice and releases its
   * character (`GameSession.depart`); `retry` joins again, with the token the page then presents.
   *
   * @returns A promise that settles once the character is released, or after the bound; it never rejects.
   */
  public depart(): Promise<void> {
    return this.session.depart();
  }

  /**
   * Asks the gateway what a chosen leave would meet, or leaves (ADR-084's amendment of 2026-10-13,
   * `GameSession.askLeave`).
   *
   * @param confirm False to ask and change nothing, true to leave.
   * @returns The answer, or null when there was none.
   */
  public askLeave(confirm: boolean): Promise<LeaveAnswer | null> {
    return this.session.askLeave(confirm);
  }

  /**
   * Tells the gateway the player leaves the login queue (P1.4-T164, `GameSession.leaveQueue`).
   *
   * @returns Whether the gateway was told.
   */
  public leaveQueue(): boolean {
    return this.session.leaveQueue();
  }

  /**
   * The page's sign-in ended while it played (P1.9-T15): the page leaves the world as a close would and never
   * joins again (`GameSession.endSignIn`).
   *
   * @param end Why the sign-in ended.
   */
  public endSignIn(end: SignInEnd): void {
    this.session.endSignIn(end);
  }

  /**
   * Registers what ends with the stage: it runs when the scene shuts down and when the page leaves the map for another
   * (`enterMap`), in the order registered.
   *
   * @param end What to run.
   */
  private endsWithStage(end: () => void): void {
    this.stageEnds.push(end);
  }

  /**
   * Ends the stage: what `prepare` registered runs, and the scene holds no map. On a change of map the prop views the
   * stage drew in are emptied and dropped as well, so nothing of the map left stays on the canvas; its painters' chunk
   * textures wait, hidden, for the next stage's painters. At shutdown the textures kept for them are destroyed.
   *
   * @param keepScene True when the scene goes on, as on a change of map; false at shutdown, where Phaser destroys the
   *   game objects itself.
   */
  private endStage(keepScene: boolean): void {
    for (const end of this.stageEnds.splice(0)) {
      end();
    }
    this.stage = null;
    if (keepScene) {
      for (const view of this.props.values()) {
        view.clear();
      }
      this.props.clear();
      this.silhouette?.hide();
    } else {
      this.chunkTextures.clear();
    }
  }

  /**
   * Stands the scene on another map of the loaded world (ADR-122 decision 2.9): the stage of the map left is ended, the
   * map's chunks are fetched, and a new stage is prepared. The scene holds no stage while the chunks load. A map the
   * scene already stands on is left as it is.
   *
   * @param mapId The map's id.
   * @throws Error naming what failed to load, as the world boot does.
   */
  private async enterMap(mapId: string): Promise<void> {
    if (this.stage?.map.id === mapId) {
      return;
    }
    this.endStage(true);
    this.prepare(await this.worldBoot.runMap(mapId));
  }

  /**
   * Fades the whole page, the HUD included, to black or back from it (`screen-fade.ts`).
   *
   * @param direction `out` to black, `in` from black.
   * @param ms The fade's length in milliseconds.
   * @returns A promise that settles when the fade is complete.
   */
  private fadePage(direction: "out" | "in", ms: number): Promise<void> {
    return fadeScreen(direction, ms);
  }

  /**
   * Takes the booted map into the scene, once for each stage: the grid and chunks, the tile animator, the presence props, the
   * target lookup, the things and their playback, the chunk painter, the occluder fade, the concealment of other
   * characters and its report, the camera's bounds and zoom, the players band, and the projectile view, whose shots
   * sort as characters do and whose landing rings lie at the ground frames' depth, over the band's flat layers and
   * under everything that stands (ADR-095 decisions 20 and 21).
   *
   * @param booted The booted world.
   */
  private prepare(booted: BootedWorld): void {
    if (this.stage !== null) {
      return;
    }
    const world = booted.world;
    const map = booted.map;
    const appearances = world.manifest.objectAppearances ?? [];
    gameReport.objectTypes = booted.types.source;
    gameReport.lighting.indoor = indoorOfMap(world.manifest.maps, world.manifest.lightMarks === true, map.id);
    gameReport.worldVersion = world.version;
    gameBanner.print(typeof __BUILD_SHA__ === "undefined" ? undefined : __BUILD_SHA__, world.version);
    const animator = new TileAnimator(world.assets);
    const presence = new PresenceProps(this, world.assets, appearances, world.manifest.tileSize, map.height, animator, presenceEntries(booted.chunks.values(), appearances));
    presence.useMasks(booted.masks?.maskOf ?? null);
    const lookup = createTargetLookup({ appearances, types: booted.types.types, frames: world.assets.frames, tileSize: world.manifest.tileSize });
    watchNpcHeads(lookup, (appearance) => this.npcHead(appearance));
    const layers = booted.chunks.values().next().value?.layers ?? [];
    const band = map.playersAbove ?? defaultPlayersAbove(layers);
    const props = this.props;
    const mapLights = readMapLights(booted.chunks.values(), world.assets, world.manifest.tileSize);
    applyKnownSources(PAGE_MAP_LIGHTS, mapLights);
    PAGE_MAP_LIGHTS.lights = mapLights;
    const shown = { id: "", frame: "" };
    const showOn = (view: PropView): void => view.showFrame(shown.id, shown.frame);
    PAGE_MAP_LIGHTS.show = (id, frame) => {
      shown.id = id;
      shown.frame = frame;
      props.forEach(showOn);
    };
    this.endsWithStage(() => {
      PAGE_MAP_LIGHTS.lights = null;
      PAGE_MAP_LIGHTS.out.clear();
      PAGE_MAP_LIGHTS.show = () => undefined;
    });
    const things = new WorldThings(this, world, map, animator, props, band, booted.baseCells, booted.grid.cells, lookup, () => PAGE_MOTION.reduced());
    const painter = new ChunkPainter(this, world, map, booted.chunks, animator, presence, props, band, CHUNK_LOADING, () => performance.now(), null, this.chunkTextures);
    this.endsWithStage(() => painter.clear());
    const harvest = this.options.harvest;
    let harvestStage: HarvestStage | null = null;
    if (harvest !== undefined) {
      harvest.useWorld(readHarvestMarks(booted.chunks, world.assets.frames, world.manifest.tileSize));
      this.endsWithStage(() => harvest.clearWorld());
      const leaves = new HarvestLeaves(this, (feetY) => this.rowDepth(feetY));
      harvestStage = bindHarvestStage({
        store: harvest,
        local: this.session.local,
        canvas: { shake: (id) => void painter.shake(id, HARVEST_SHAKE_PIXELS, HARVEST_SHAKE_MS), leaves: (x, y, feetY) => leaves.burst(x, y, feetY) },
        now: () => this.game.loop.time,
        reduced: () => PAGE_MOTION.reduced(),
      });
      this.endsWithStage(() => harvestStage?.end());
    }
    const minimap = this.options.minimap;
    const landmarkWorld = { manifest: world.assets, appearances, tileSize: world.manifest.tileSize, chunkSize: world.manifest.chunkSize };
    const shrineArt = new ShrineArt(world.manifest.chunkSize);
    const landmarkTally: LandmarkTally = { skipped: 0 };
    let pictures: GameMapPictures | null = null;
    let worldPicture: WorldMapPicture<PageImage> | null = null;
    if (minimap !== undefined) {
      const made = new GameMapPictures({ id: map.id, width: map.width, height: map.height, chunkSize: world.manifest.chunkSize, tileSize: world.manifest.tileSize }, booted.chunks, world.assets, createDomSurface, scenePageAtlas(this, world.assets), undefined, undefined, () => this.session.revealed, undefined, { appearances, art: shrineArt, tally: landmarkTally });
      pictures = made;
      minimap.setGround(made);
      minimap.setPlace(placeName(regionOf(map), mapTitle(world.manifest.worldId, map)));
      gameReport.minimapOthers = () => minimapOthersReading(minimap.others);
      gameReport.minimapGround = () => ({ pictures: made.size, bytes: made.bytes, owedHud: made.owed("hud"), owedNearby: made.owed("nearby"), composed: made.composed, most: made.most, shrineArt: shrineArt.list.length, skippedLandmarks: made.skippedLandmarks });
      this.endsWithStage(() => {
        minimap.setGround(null);
        minimap.setPin(null);
        minimap.setPlace("");
        minimap.beginOthers();
        minimap.endOthers();
        gameReport.minimapGround = () => null;
        gameReport.minimapOthers = () => ({ players: [], creatures: [], dropped: 0 });
        made.clear();
      });
    }
    if (this.options.worldMap !== undefined) {
      const worldMap = this.options.worldMap;
      const made = createWorldMapPicture(this, map, { chunks: booted.chunks, assets: world.assets, appearances, chunkSize: world.manifest.chunkSize, tileSize: world.manifest.tileSize, art: shrineArt, tally: landmarkTally });
      worldPicture = made;
      worldMap.setWorld({ worldId: world.manifest.worldId, mapId: map.id, title: mapTitle(world.manifest.worldId, map), width: map.width, height: map.height, chunkSize: world.manifest.chunkSize, picture: made });
      const drawAhead = createIdleDrawer(worldMap, frameIdleScheduler(window));
      const stopArt = worldMap.onPlaces((places) => {
        const boxes = shrineArt.replace(places?.places ?? [], landmarkWorld);
        pictures?.redraw(boxes);
        worldPicture?.redraw(boxes);
      });
      this.endsWithStage(() => {
        stopArt();
        drawAhead.stop();
        worldMap.clearWorld();
      });
    }
    const beside = mountBeside({ scene: this, boot: this.worldBoot, world, map, band, animator, loading: CHUNK_LOADING, textures: this.chunkTextures, now: () => performance.now(), active: () => this.session.revealed });
    if (pictures !== null && beside.minimap !== null) {
      minimap?.setGround(beside.minimap.around(pictures, this.crossing.restaging));
    }
    this.endsWithStage(() => beside.clear());
    const grid = this.crossing.mount(booted, beside);
    this.endsWithStage(followWorldZoom(this.cameras.main, this.options.zoom));
    this.cameras.main.setRoundPixels(true);
    gameReport.chunksLoaded = booted.chunks.size;
    const fade = new PropFade(this.textures, world.assets, booted.masks, props, presence, world.manifest.tileSize, SIGHT_CONE, { width: map.width * world.manifest.tileSize, height: map.height * world.manifest.tileSize });
    const concealment = new Concealment({ mostCovered: (x, y, width, height, depth, kept) => fade.mostCovered(x, y, width, height, depth, kept), roofedApart: (x, y) => presence.roofedApart(x, y), version: () => fade.version, inSight: (x, y) => fade.inSight(x, y) });
    gameReport.concealment = () => concealReadings(this.session.remotes.drawn);
    const projectiles = new ProjectileView({ scene: this, looks: booted.projectiles, rules: PAGE_RULES, pixelsPerSixteenth: world.manifest.tileSize / SIXTEENTHS, depth: (groundY) => this.rowDepth(groundY), groundDepth: layerDepth(band, band) + sortFraction(GROUND_ROW, map.height), now: () => performance.now(), report: gameReport.projectiles });
    this.endsWithStage(() => projectiles.destroy());
    const ground = new GroundView(this, PAGE_ICONS, PAGE_RULES, world.manifest.tileSize, layerDepth(band, band) + sortFraction(GROUND_ROW, map.height), layerDepth(band, band) + sortFraction(GROUND_ITEM_ROW, map.height), () => PAGE_MOTION.reduced());
    this.endsWithStage(() => ground.destroy());
    const puddleGround = puddleGroundOf({ tileSize: world.manifest.tileSize, chunkSize: world.manifest.chunkSize, width: map.width, height: map.height, chunks: booted.chunks, surfaces: booted.surfaces, collision: booted.baseCells, assets: world.assets, band });
    const feed = weatherFeed({ table: DEFAULT_WEATHER, worldSeed: worldWeatherSeed(world.manifest.worldId), regionId: weatherRegionOf(map.id, map.group?.id ?? null), forced: forcedFor(this.options.weatherForce, map.id), forcedWet: forcedFor(this.options.wetForce, map.id), forcedWind: this.options.windForce ?? null, forcedClouds: this.options.cloudsForce ?? null });
    const puddleList = this.crossing.puddles(world, map.id, () => placePuddles(puddleGround, worldWeatherSeed(world.manifest.worldId), map.id));
    const roofColumns = map.width * COLLISION_CELLS_PER_TILE;
    const roofRows = map.height * COLLISION_CELLS_PER_TILE;
    const roofRooms = presence.roomPlane(roofColumns, roofRows);
    presence.useRooms(roofRooms, roofColumns, roofRows);
    const stand: WeatherStand = { strength: feed.strength, flash: feed.flash, wetStart: feed.wetStart, wetFrozen: feed.wetFrozen, drip: { level: this.drenchedLevel, roofed: (x, y) => roofedAt(roofRooms, roofColumns, roofRows, Math.round((x * SIXTEENTHS) / world.manifest.tileSize), Math.round((y * SIXTEENTHS) / world.manifest.tileSize)), blockedAt: puddleBlockedAt(puddleGround) }, roof: { inside: () => presence.sheltered, area: presence.open, stamps: () => presence.softStamps }, puddles: { list: puddleList, depth: layerDepth(band, band) + sortFraction(GROUND_ROW, map.height), buried: { ready: () => presence.placesReady, at: (puddle) => presence.coversAny(puddle.x, puddle.y) } }, ...(booted.windSheets === undefined ? {} : { wind: { sheets: booted.windSheets, wind: feed.wind, clouds: feed.clouds, seed: worldWeatherSeed(world.manifest.worldId), region: regionWord(weatherRegionOf(map.id, map.group?.id ?? null)) } }) };
    const kept = this.weatherHandover.take();
    kept?.stand(stand, WEATHER_FADE_MS);
    const weather = kept ?? new WeatherView({ scene: this, camera: this.cameras.main, now: this.options.now ?? serverNow, reducedMotion: () => PAGE_MOTION.reduced(), effects: this.options.effects ?? (() => "full"), indoor: () => gameReport.lighting.indoor !== null, feet: { mode: this.options.feetMode ?? "auto", figures: this.eachFigure }, ...stand });
    gameReport.puddles = () => ({ wet: weather.wet, puddles: puddleList.filter((p) => weather.holdsPuddle(p)).length, shown: weather.puddlesShown, groundTiles: puddleGround.ground.reduce((n, g) => n + (g === 0 ? 0 : 1), 0), list: puddleList.filter((p) => weather.holdsPuddle(p)).map((p) => ({ x: p.x, y: p.y, radius: p.radius, cls: p.cls })), drips: weather.dripPuddles, dripDrops: weather.dripDrops });
    this.endsWithStage(() => {
      gameReport.puddles = () => null;
      presence.destroy();
      if (this.weatherHandover.keeps(weather)) return;
      weather.destroy();
      clearWeather(gameReport.weather, gameReport.lighting);
    });
    this.stage = { world, map, mapId: map.id, maps: world.manifest.maps, version: world.version, tileSize: world.manifest.tileSize, grid, surfaces: booted.surfaces, looks: booted.looks, npcLooks: booted.npcLooks, manifest: booted.characters, ownLook: booted.ownLook, animator, presence, lookup, things, painter, beside, fade, concealment, silhouette: (this.silhouette ??= new Silhouette(this, SILHOUETTE, Phaser.TintModes.FILL)), projectiles, ground, band, harvest: harvestStage, stand: booted.stand, weather };
  }

  /**
   * After each drawn frame: records what the frame's camera showed for the report, and before the reveal,
   * reveals the world when the frame shows the local character over every chunk its camera saw, after the
   * welcome and the first snapshot. The camera's `worldView` here is the one the frame was drawn with.
   */
  private readonly afterRender = (): void => {
    const stage = this.stage;
    if (stage === null) {
      return;
    }
    const session = this.session;
    const range = stage.painter.range;
    const seen = this.cameras.main.worldView;
    const drawn = gameReport.drawn;
    drawn.x = seen.x;
    drawn.y = seen.y;
    drawn.width = seen.width;
    drawn.height = seen.height;
    drawn.painted = range !== null;
    drawn.minCx = range?.minCx ?? 0;
    drawn.minCy = range?.minCy ?? 0;
    drawn.maxCx = range?.maxCx ?? 0;
    drawn.maxCy = range?.maxCy ?? 0;
    drawn.character = session.local.sprite !== null;
    session.mend();
    const arriving = session.arriving;
    if ((session.revealed && !arriving) || !session.welcomed) {
      this.ownLookSince = -1;
      return;
    }
    const chunkSize = stage.world.manifest.chunkSize;
    const needed = chunksCovering({ left: seen.x, top: seen.y, width: seen.width, height: seen.height }, stage.world.manifest.tileSize, chunkSize, Math.ceil(stage.map.width / chunkSize), Math.ceil(stage.map.height / chunkSize));
    if (!readyToReveal({ welcomed: session.welcomed, snapshot: session.snapshotSeen, character: session.local.sprite !== null && session.snapshotSeen && this.ownFigureShows(), painted: range, needed })) {
      return;
    }
    if (arriving) {
      session.arrived();
    } else {
      session.reveal();
    }
  };

  /**
   * Sends the frame's inputs, then fires a click held for its swing's cooldown toward the pointer once the swing is
   * ready (`fight/fight.ts`), so its act follows the input just sent; draws the local character (`local-player.ts`),
   * fades the props over it and shows its silhouette through a building part that hides it unless a closed roof covers it too (`silhouette.ts`), rounds the camera to the same whole pixels as the sprite (so a diagonal never rounds them
   * apart), poses every other character on the same footing, its offset from the local character's rendered position
   * rounded once (`remote-characters.ts`), tells the target frame what the view holds for the
   * target (`page-targets.ts`), plays the things' transitions, draws the shots in flight and their bursts, and keeps
   * chunks painted, plays the roofs for the local
   * character, then judges which other characters the viewer cannot see (`concealment.ts`), which the world UI reads
   * after this update. An open reading panel closes on the frame its thing no longer reaches the predicted character (P1.9-T37).
   *
   * @param time Scene time in ms.
   * @param delta Frame delta in ms.
   */
  public override update(time: number, delta: number): void {
    const stage = this.stage;
    const session = this.session;
    const local = session.local;
    const predictor = local.prediction;
    const connection = session.connection;
    const intent = this.intent;
    if (stage === null || predictor === null || connection === null || intent === null || !session.remotes.active || session.holding) {
      stage?.weather.setPaused(true);
      this.companions?.clear();
      clearWeather(gameReport.weather, gameReport.lighting);
      return;
    }
    stage.weather.setPaused(false);
    stage.animator.update(time);
    this.options.run?.hold(connection.view.entities.get(session.handle)?.away === Away.Held);
    local.sendInputs(predictor, delta, intent, connection, this.run);
    const pointer = this.input.activePointer;
    const aim = this.cameras.main.getWorldPoint(pointer.x, pointer.y, this.pressed);
    session.fireHeld(aim.x, aim.y);
    const scale = stage.world.manifest.tileSize / SIXTEENTHS;
    local.draw(predictor, delta, time, scale);
    stage.harvest?.step();
    session.ownStep(time);
    session.listen();
    const px = local.drawn.x;
    const py = local.drawn.y;
    const sprite = local.sprite;
    if (sprite !== null) {
      stage.fade.fadeFor(px, py, sprite.depth, local.heading(), delta);
      gameReport.sightHeading = stage.fade.sightHeading;
      gameReport.sightDimmed = stage.fade.sightDimmed;
      gameReport.sightCover = stage.fade.sightCover;
      gameReport.sightDense = stage.fade.sightDense;
      gameReport.sightStrength = stage.fade.sightStrength;
      stage.silhouette.sync(sprite, stage.fade.roofCover > 0 ? 0 : stage.fade.keptCover);
      gameReport.keptCover = stage.fade.keptCover;
      gameReport.roofCover = stage.fade.roofCover;
    } else {
      stage.silhouette.hide();
    }
    gameReport.silhouette = stage.silhouette.visible;
    const camera = this.cameras.main;
    const focus = this.options.director?.focus() ?? null;
    camera.centerOn(focus?.x ?? px, focus?.y ?? py);
    const snapped = snapScrollToDevicePixels(camera);
    camera.setScroll(snapped.scrollX, snapped.scrollY);
    const entities = connection.view.entities;
    const looks = this.options.looks;
    looks?.frame(px, py, time);
    looks?.pin(sprite);
    session.remotes.draw(delta, time, scale, entities, session.handle, px, py, local.rendered.x, local.rendered.y);
    session.crossing.draw(delta, time, scale, px, py, local.rendered.x, local.rendered.y);
    looks?.pin(session.remotes.drawn.get(session.targets.handle)?.sprite);
    this.companionFeedOn(stage).frame(entities, session.handle, local, session.remotes.drawn, delta, time, this.options.companion?.getSnapshot().name ?? "", aim.x, aim.y);
    session.targets.show();
    stage.things.play(this.game.loop.time);
    stage.projectiles.update();
    stage.ground.frame(this.game.loop.time);
    this.options.reading.keepWhile(this.interaction.readingReaches);
    this.options.chest?.keepWhile(this.interaction.readingReaches);
    const me = entities.get(session.handle);
    if (me !== undefined) {
      gameReport.pvp = me.pvp;
      this.options.hud.setFlag(me.pvp);
      this.options.hud.setLevel(me.level);
    }
    this.options.worldMap?.setPosition(px / stage.world.manifest.tileSize, py / stage.world.manifest.tileSize, local.heading());
    this.options.worldMap?.frame();
    gameReport.localX = predictor.predicted.x;
    gameReport.localY = predictor.predicted.y;
    gameReport.localStrip = sprite?.strip ?? "";
    gameReport.localLift = sprite?.lift ?? 0;
    session.remotes.report();
    gameReport.speed = WALK_SPEED;
    stage.painter.sync(camera);
    stage.beside.sync(camera);
    stage.weather.update();
    publishWeather(stage.weather, gameReport.weather, gameReport.lighting);
    stage.presence.update(Math.round(local.rendered.x), Math.round(local.rendered.y), time);
    gameReport.lighting.roofStamps = stage.presence.stamps;
    gameReport.presenceTransitions = stage.presence.started;
    stage.concealment.judge(session.remotes.drawn, session.targets.handle, scale, time);
    gameReport.concealJudged = stage.concealment.measured;
    const minimap = this.options.minimap;
    if (minimap !== undefined) {
      this.minimapOthers.update(minimap, session.remotes, entities, (appearance) => stage.looks[appearance] ?? null, px, py, stage.world.manifest.tileSize, this.npcLookOf, this.besideOthers);
      minimap.setPin(this.options.worldMap?.pinNow() ?? null);
      minimap.setPosition(px / stage.world.manifest.tileSize, py / stage.world.manifest.tileSize);
    }
    this.debug.draw(stage.grid, px, py, scale);
  }

  /**
   * The label over a companion while the pointer is on him, a small text in the world.
   *
   * @returns The label.
   */
  private companionLabel(): CompanionLabel {
    const text = this.add.text(0, 0, "", { fontFamily: "monospace", fontSize: "8px", color: "#f6e7c1", stroke: "#1b1208", strokeThickness: 3 }).setOrigin(0.5, 1).setDepth(1_000_000).setVisible(false);
    this.endsWithStage(() => text.destroy());
    return {
      show: (words, x, y) => {
        text.setText(words).setPosition(x, y).setVisible(true);
      },
      hide: () => {
        text.setVisible(false);
      },
    };
  }

  /**
   * The visit that feeds the companions each frame, made with them.
   *
   * @param stage The stage.
   * @returns The feed.
   */
  private companionFeedOn(stage: Stage): CompanionFeed {
    const followers = this.companionsOn(stage);
    this.companionFeed ??= new CompanionFeed(followers);
    return this.companionFeed;
  }

  /**
   * The companions drawn on this stage (ADR-142 decision 7.2), made with the first frame of a map and gone with it.
   *
   * @param stage The stage.
   * @returns The companions.
   */
  private companionsOn(stage: Stage): CompanionFollowers {
    if (this.companions === null) {
      const followers = new CompanionFollowers((sheet, scale) => (stage.manifest.characters[sheet] === undefined ? null : new CharacterSprite(this, stage.manifest, [sheet], (feetX, feetY) => this.characterDepth(feetX, feetY)).setDrawScale(scale)), stage.tileSize, PAGE_RULES.constants.companion_fly_gap, (own, source) => (own ? source || say("companion.fallbackName") : say("companion.hover", { owner: source })), this.companionLabel(), () => this.options.lowDetail?.() !== true);
      this.companions = followers;
      this.endsWithStage(() => {
        followers.clear();
        this.companions = null;
        this.companionFeed = null;
      });
    }
    return this.companions;
  }

  /**
   * Whether the frame just drawn showed the page's own character, for the reveal (`reveal.ts`): a composed figure
   * draws nothing until the fallback look's block is drawn, and shows the fallback look until its own look is
   * composed, which the reveal waits for so a player first sees the character as itself. The wait for the own look
   * is bounded (`OWN_LOOK_WAIT_MS` from the first snapshot): a look whose frame was dropped, or whose sheets are slow,
   * reveals the world with the character in the fallback look, which its own look replaces when it comes.
   *
   * @returns True when the reveal need not wait for the looks.
   */
  private ownFigureShows(): boolean {
    const looks = this.options.looks;
    if (looks === undefined) {
      return true;
    }
    if (!looks.ready) {
      return false;
    }
    const now = this.game.loop.time;
    if (this.ownLookSince < 0) {
      this.ownLookSince = now;
    }
    return looks.dressed(this.session.local.sprite) || now - this.ownLookSince >= OWN_LOOK_WAIT_MS;
  }

  /**
   * How far a player's art reaches above its feet: the look layout's one head height when the page draws composed
   * looks (ADR-094 decision 34), the plain figure's otherwise.
   *
   * @returns The height in world pixels.
   */
  private playerHead(): number {
    return this.options.looks?.head() ?? HEAD_ABOVE_FEET;
  }

  /**
   * Creates a player's character: a composed figure in the fallback look, which the session tells the look its
   * handle holds (ADR-094 decisions 6 and 14). The world boot fails before a scene joins when the looks cannot be
   * started, so a figure is always to be had here (ADR-094 note 88). Its depth
   * is `standingDepth` of its feet line, the rule props sort by, so a character walks behind a prop anchored below its
   * feet, in front of one anchored above, and under one whose ground line is its feet line (ADR-071 decision 2).
   *
   * @returns The figure.
   * @throws Error when the manifest is not loaded, or the looks are not started.
   */
  private character(): CharacterFigure {
    const stage = this.stage;
    if (stage === null) {
      throw new Error("character manifest is not loaded");
    }
    const figure = this.options.looks?.player((feetX, feetY) => this.characterDepth(feetX, feetY)) ?? null;
    if (figure === null) {
      throw new Error("the looks are not started, so there is no figure to draw a player with");
    }
    return figure;
  }

  /**
   * Creates the page's own character: the look the world boot loaded for it (a local trial look, P1.9-T88), its layers
   * placed as the look says, or else what every other player is drawn as (`character()`).
   *
   * @returns The sprite.
   * @throws Error when the manifest is not loaded.
   */
  private ownCharacter(): CharacterFigure {
    const stage = this.stage;
    return ownOrPlain(
      stage?.ownLook ?? null,
      () => this.character(),
      (look) => new CharacterSprite(this, (stage as Stage).manifest, look.layers, (feetX, feetY) => this.characterDepth(feetX, feetY), look.places),
    );
  }

  /**
   * Creates a creature with the character sheet its look names (ADR-082 decision 6), sorting as a character does.
   *
   * @param appearance The creature's appearance, its index in the version's look table.
   * @param handle The creature's handle, which names its look when the page draws the composed crowd.
   * @returns The sprite, or null when the table has no such look or the character manifest no such sheet.
   * @throws Error when the manifest is not loaded.
   */
  private creature(appearance: number, handle: number): CharacterFigure | null {
    const stage = this.stage;
    if (stage === null) {
      throw new Error("character manifest is not loaded");
    }
    const composed = this.options.looks?.creature(handle, (feetX, feetY) => this.characterDepth(feetX, feetY)) ?? null;
    if (composed !== null) {
      return composed;
    }
    const sheet = stage.looks[appearance]?.sheet;
    if (sheet === undefined || stage.manifest.characters[sheet] === undefined) {
      return null;
    }
    return new CharacterSprite(this, stage.manifest, [sheet], (feetX, feetY) => this.characterDepth(feetX, feetY));
  }

  /**
   * How far a creature's art reaches above its feet, measured once per sheet (`creature-heads.ts`).
   *
   * @param appearance The creature's appearance, its index in the version's look table.
   * @returns The height in world pixels, the default `HEAD_ABOVE_FEET` when the look or its sheet is missing.
   */
  private creatureHead(appearance: number): number {
    const composed = this.options.looks?.creatureHead() ?? null;
    if (composed !== null) {
      return composed;
    }
    const stage = this.stage;
    const sheet = stage?.looks[appearance]?.sheet;
    if (stage === null || sheet === undefined) {
      return HEAD_ABOVE_FEET;
    }
    this.heads ??= new CreatureHeads(this.textures);
    return this.heads.of(stage.manifest, sheet);
  }

  /**
   * An NPC's name from the loaded world's NPC look table (ADR-136 decision 2.4), which the dialogue window names its
   * speaker by.
   *
   * @param appearance The NPC's appearance, its index in the table.
   * @returns The name, or "" before the world loads or past the table's end.
   */
  public npcName(appearance: number): string {
    return this.stage === null ? "" : npcName(this.stage.npcLooks, appearance);
  }

  /**
   * An NPC's portrait for the dialogue window (`npc-figures.ts`).
   *
   * @param appearance The NPC's appearance, its index in the NPC look table.
   * @returns The portrait, or null before the world loads, past the table's end or for a sheet with no idle strip.
   */
  public npcPortrait(appearance: number): DialoguePortrait | null {
    return this.stage === null ? null : npcPortrait(this.stage.manifest, this.stage.npcLooks, appearance, this.options.base);
  }

  /**
   * Creates an NPC's figure at its look's scale (`npc-figures.ts`), sorting as a character does.
   *
   * @param appearance The NPC's appearance, its index in the version's NPC look table.
   * @returns The sprite, or null when the table has no such look or the character manifest no such sheet.
   * @throws Error when the manifest is not loaded.
   */
  private npc(appearance: number): CharacterFigure | null {
    const stage = this.stage;
    if (stage === null) {
      throw new Error("character manifest is not loaded");
    }
    return npcFigure(this, stage.manifest, stage.npcLooks, appearance, (feetX, feetY) => this.characterDepth(feetX, feetY));
  }

  /**
   * How far an NPC's art reaches above its feet at its look's scale (`npc-figures.ts`).
   *
   * @param appearance The NPC's appearance, its index in the version's NPC look table.
   * @returns The height in world pixels, the default `HEAD_ABOVE_FEET` before the world loads or past the table's end.
   */
  private npcHead(appearance: number): number {
    const stage = this.stage;
    if (stage === null) {
      return HEAD_ABOVE_FEET;
    }
    this.heads ??= new CreatureHeads(this.textures);
    return npcHead(this.heads, stage.manifest, stage.npcLooks, appearance);
  }

  /**
   * The depth a character whose feet line is `feetY` draws at by its row alone: `standingDepth` over this world's band
   * and map height. Projectiles sort by it, as they stand on no painted ground.
   *
   * @param feetY The feet line in world pixels.
   * @returns The depth.
   */
  private rowDepth(feetY: number): number {
    const stage = this.stage;
    return standingDepth(feetY, stage?.world.manifest.tileSize ?? 1, stage?.band ?? 0, stage?.map.height ?? 0);
  }

  /**
   * The depth a character with its feet at a point draws at (ADR-132 decisions 3 and 4): just under the depth of the
   * prop whose Behind cell the feet stand on, just over it on an In front cell, and its own row's (`rowDepth`)
   * anywhere else. The one rule players, creatures and the fade over the local character sort by.
   *
   * @param feetX The feet x in world pixels.
   * @param feetY The feet line in world pixels.
   * @returns The depth.
   */
  private characterDepth(feetX: number, feetY: number): number {
    return standDepthAt(this.stage?.stand ?? null, feetX, feetY, this.stage?.world.manifest.tileSize ?? 1, this.rowDepth(feetY));
  }

  /**
   * The pieces that would cover a character standing at a feet point (ADR-071 decision 9, `prop-fade.ts`): every
   * prop, presence prop and thing the page draws now against the depth `character()` hands a sprite on that feet
   * line. Read-only: nothing moves.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @returns The covering pieces, or none before the world has loaded.
   */
  public coveringAt(x: number, y: number): CoveringPiece[] {
    return this.stage?.fade.coveringAt(x, y, this.characterDepth(x, y)) ?? [];
  }

  /**
   * The alpha every prop, presence prop and thing draws at once the occluder fade has run for a character
   * standing at a feet point (ADR-071 decision 10, `prop-fade.ts`), at the depth `character()` hands a sprite on
   * its feet line; nothing moves.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @returns Each shown piece and its alpha, or none before the world has loaded.
   * @throws RangeError when the feet are off whole pixels and a prop reaches the pixel count.
   */
  public fadingAt(x: number, y: number): PieceAlpha[] {
    return this.stage?.fade.fadingAt(x, y, this.characterDepth(x, y)) ?? [];
  }

  /**
   * The largest share of a player character's body standing at a feet point one prop drawn above it hides, as the
   * concealment of other characters measures it (ADR-061's amendment for P1.9-T73), over the props the page draws now
   * and at the alphas the fade left them. Read-only, for browser suites choosing where a character stands hidden.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @returns The share in `[0, 1]`, or 0 before the world has loaded.
   */
  public coverAt(x: number, y: number): number {
    return this.stage?.fade.mostCovered(x, y, BODY_WIDTH, BODY_HEIGHT, this.characterDepth(x, y)) ?? 0;
  }

  /**
   * The largest share of a player character's body standing at a feet point one building part drawn above it hides
   * (ADR-040's amendment of 2026-10-03), measured as `coverAt` measures. Read-only and made on request, for browser
   * suites choosing where a character stands behind a wall.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @returns The share in `[0, 1]`, or 0 before the world has loaded.
   */
  public keptAt(x: number, y: number): number {
    const kept = { share: 0 };
    this.stage?.fade.mostCovered(x, y, BODY_WIDTH, BODY_HEIGHT, this.characterDepth(x, y), kept);
    return kept.share;
  }
}
